import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCreditCard, FiDownload, FiCalendar, FiDollarSign, FiCheck, FiX, FiFilter } = FiIcons;

const PaymentHistory = () => {
  const { role } = useAuth();
  const [filter, setFilter] = useState('all');

  // Mock payment data
  const payments = [
    {
      id: '1',
      eventTitle: 'Wealth Building Masterclass',
      amount: 97.00,
      status: 'completed',
      date: '2024-01-15T10:30:00Z',
      paymentMethod: 'Visa ending in 4242',
      transactionId: 'txn_1234567890'
    },
    {
      id: '2',
      eventTitle: 'Investment Strategies Workshop',
      amount: 149.00,
      status: 'completed',
      date: '2024-01-10T14:20:00Z',
      paymentMethod: 'Mastercard ending in 8888',
      transactionId: 'txn_0987654321'
    },
    {
      id: '3',
      eventTitle: 'Financial Planning Bootcamp',
      amount: 0.00,
      status: 'completed',
      date: '2024-01-05T09:15:00Z',
      paymentMethod: 'Free Event',
      transactionId: 'reg_free_001'
    }
  ];

  const filteredPayments = payments.filter(payment => {
    if (filter === 'all') return true;
    if (filter === 'paid') return payment.amount > 0;
    if (filter === 'free') return payment.amount === 0;
    return payment.status === filter;
  });

  const totalSpent = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalEvents = payments.length;
  const paidEvents = payments.filter(p => p.amount > 0).length;

  const stats = [
    {
      title: 'Total Spent',
      value: `$${totalSpent.toFixed(2)}`,
      icon: FiDollarSign,
      color: 'bg-green-500'
    },
    {
      title: 'Events Attended',
      value: totalEvents,
      icon: FiCalendar,
      color: 'bg-blue-500'
    },
    {
      title: 'Paid Events',
      value: paidEvents,
      icon: FiCreditCard,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment History</h1>
          <p className="text-gray-600 mt-1">View all your event payments and transactions</p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
          <SafeIcon icon={FiDownload} className="w-4 h-4" />
          <span>Export</span>
        </button>
      </div>

      {/* Stats Overview */}
      {role === 'student' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} rounded-lg p-3`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Payment History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Transactions</option>
                <option value="paid">Paid Events</option>
                <option value="free">Free Events</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipt
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment, index) => (
                <motion.tr
                  key={payment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{payment.eventTitle}</p>
                      <p className="text-sm text-gray-500">ID: {payment.transactionId}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                      <span className={`font-medium ${payment.amount === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                        {payment.amount === 0 ? 'FREE' : `$${payment.amount.toFixed(2)}`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCreditCard} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{payment.paymentMethod}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {format(new Date(payment.date), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : payment.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      <SafeIcon 
                        icon={payment.status === 'completed' ? FiCheck : payment.status === 'pending' ? FiCalendar : FiX} 
                        className="w-3 h-3 mr-1" 
                      />
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center space-x-1">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="p-12 text-center">
            <SafeIcon icon={FiCreditCard} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No transactions found</p>
            <p className="text-gray-400">Your payment history will appear here</p>
          </div>
        )}
      </div>

      {/* Payment Methods */}
      {role === 'student' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Saved Payment Methods</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">VISA</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">•••• •••• •••• 4242</p>
                    <p className="text-sm text-gray-500">Expires 12/25</p>
                  </div>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                    Default
                  </span>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
              
              <button className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors">
                + Add New Payment Method
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
